import React, { useState } from 'react'
import { Route, NavLink } from 'react-router-dom'

import { routes, localStorageKeys } from '@sketch/constants'
import { useFlag } from 'containers/flags'
import General from '../../pages/GeneralSettings'
import AppearanceSettings from '../../pages/AppearanceSettings'
import ManagePersonalTokensView from 'modules/personalTokens/views/ManagePersonalTokensView'

import { PageWrapper } from './SettingsView.styles'
import { useGetWorkspaces } from 'modules/workspace/operations'
import { getItem } from '@sketch/utils'

import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import GenericErrorView from 'views/GenericErrorView'

import WorkspaceSidebarLayout from 'modules/workspace/components/WorkspaceSidebarLayout'

import Navbar from 'components/Navbar'
import { Segment } from '@sketch/components'

const SettingsView = () => {
  const isDarkModeToggleEnabled = useFlag('dark-mode-toggle')
  const [workspaceId] = useState<string | undefined>(
    getItem(localStorageKeys.lastWorkspaceIdKey)
  )
  const { workspaces, loading, error } = useGetWorkspaces()
  const personalTokenManagementEnabled = useFlag(
    'public-api-manage-personal-tokens'
  )

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error) {
    return <GenericErrorView error={error} />
  }

  const currentWorkspace =
    workspaces?.find(({ identifier }) => workspaceId === identifier) ||
    // This view can be accessed when there is no workspaceId selected
    // (i.e. not stored in the local storage)
    // Therefore let's use first workspace from the list if this happens
    // (if there will be any items in the list)
    workspaces?.[0]

  const navbar = (
    <Navbar.Section ml={2}>
      <Segment
        type="text"
        label="General"
        render={(className, children) => (
          <NavLink
            exact
            className={className}
            to={routes.PERSONAL_SETTINGS.create({})}
          >
            {children}
          </NavLink>
        )}
      />
      {isDarkModeToggleEnabled && (
        <Segment
          type="text"
          label="Appearance"
          render={(className, children) => (
            <NavLink
              exact
              className={className}
              to={routes.APPEARANCE_SETTINGS.create({})}
            >
              {children}
            </NavLink>
          )}
        />
      )}
      {personalTokenManagementEnabled && (
        <Segment
          type="text"
          label="Access Tokens"
          render={(className, children) => (
            <NavLink
              exact
              className={className}
              to={routes.PERSONAL_SETTINGS_ACCESS_TOKENS.create({})}
            >
              {children}
            </NavLink>
          )}
        />
      )}
    </Navbar.Section>
  )

  return (
    <WorkspaceSidebarLayout
      title="Settings"
      currentWorkspace={currentWorkspace}
      workspaces={workspaces ?? []}
      navbar={navbar}
    >
      <PageWrapper>
        <Route
          exact
          path={[
            routes.PERSONAL_SETTINGS.template(),
            routes.PERSONAL_SETTINGS_UNSUBSCRIBE.template(),
          ]}
        >
          <General />
        </Route>
        <Route exact path={[routes.APPEARANCE_SETTINGS.template()]}>
          <AppearanceSettings />
        </Route>
        {personalTokenManagementEnabled && (
          <Route
            exact
            path={[
              routes.PERSONAL_SETTINGS_ACCESS_TOKENS.template(),
              routes.PERSONAL_SETTINGS_ACCESS_TOKENS_CREATE.template(),
            ]}
          >
            <ManagePersonalTokensView currentWorkspace={currentWorkspace} />
          </Route>
        )}
      </PageWrapper>
    </WorkspaceSidebarLayout>
  )
}

export default SettingsView
